import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Answer, getReportNextStatus } from '../Review/utils';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/pro-light-svg-icons';
import AddAnswerModal from './AddAnswerModal';
import TrueImpactJSON from 'shared/design-system/components/TrueImpactJSON';
import { ReportStatusModal } from 'shared/components/Feedback/ReportStatus';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { ReportBuilderData } from 'shared/providers/ReportProvider/utils';
import AnswersList from './AnswersList';
import { useProgram } from 'shared/providers/ProgramProvider';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';

interface AnswersModalProps {
  open: boolean;
  report: ReportBuilderData;
  answers: Answer[];
  isLoading: boolean;
  programName: string;
  isReportPassed: boolean;
  handleClose: () => unknown;
  reloadAnswers: () => unknown;
}

const StyledDiv = styled.div`
  .question-wrapper {
    display: flex;
    gap: ${SPACERS.small};
    padding-top: ${SPACERS.small};
    padding-left: ${SPACERS.small};
    padding-right: ${SPACERS.small};
    padding-bottom: ${SPACERS.tiny};
    &:nth-child(odd) {
      background-color: #f2f2f2;
    }
    &:nth-child(even) {
      background-color: #ffffff;
    }
    .answer-wrapper {
      flex: 1;
      & * {
        font-size: 13px;
      }
      .report-status {
        margin-left: auto;
        margin-right: auto;
      }
    }
  }
  .donor-portal-info {
    padding: ${SPACERS.medium};
    & > div {
      margin-bottom: ${SPACERS.tiny};
    }
  }
  .answer-label {
    display: none;
  }
  .add-answer-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: ${SPACERS.large};
    .add-answer-button {
      svg {
        width: 20px;
        height: 20px;
        margin-bottom: -1px;
        margin-right: ${SPACERS.tiny};
      }
    }
  }
`;

const AnswersModal: React.FC<AnswersModalProps> = ({
  open,
  report,
  isLoading,
  answers: a,
  programName,
  isReportPassed,
  handleClose,
  reloadAnswers,
}) => {
  const { isCNUser, isThirdParty } = useUserActions();
  const cnReportFeedbackEnabled = useFeatureFlags('cnReportFeedback');
  const {
    reportNpoIndicators,
    reportResults,
    impactSummary,
    impactDetails,
  } = useIndicator();
  const { answers: programAnswers } = useProgram();
  const { isGhosting } = useUserActions();
  const [openAddAnswerModal, setOpenAddAnswerModal] = useState<boolean>(false);
  const [answers, setAnswers] = useState<Answer[]>(a);

  useEffect(() => {
    setAnswers(a);
  }, [a]);

  const allocation = report.allocations[0];
  const firstPortfolio = allocation?.investment.portfolio;
  const firstAllocationId = allocation?.dbId;
  const firstInvestmentId = allocation?.investment.dbId;
  const firstDonorEmail = firstPortfolio?.relationshipManager?.email;
  const donorPortalUrl =
    firstInvestmentId && firstAllocationId
      ? `${window.location.origin}/donor/impact-receipt/${firstInvestmentId}?allocationId=${firstAllocationId}`
      : undefined;

  return (
    <TrueImpactModal
      open={open}
      hideFooter={true}
      showExpandButton={true}
      onClose={handleClose}
      reload={{
        text: 'Reload',
        isLoading: isLoading,
        onClick: reloadAnswers,
      }}
      maxWidth="1100px"
    >
      <StyledDiv>
        <div className="question-wrapper">
          <div
            className="answer-wrapper"
            style={{ marginBottom: SPACERS.small }}
          >
            <TrueImpactJSON
              parseObject={{
                programAnswers,
                impactSummary,
                impactDetails,
                reportResults,
                reportNpoIndicators,
                reportDetails: {
                  feedbackType: report.feedbackType,
                  isFinalizeStage: report.stage === 'FINAL',
                  isReportPassed: isReportPassed,
                  stage: report.stage,
                  status: report.status,
                  year: report.year,
                  version: report.version,
                  startDate: report.startDate,
                  endDate: report.endDate,
                  nextStatus: getReportNextStatus(
                    report.stage,
                    report.status,
                    report.feedbackType,
                    isCNUser,
                    isThirdParty,
                    cnReportFeedbackEnabled,
                    isReportPassed,
                    report.totalDonations || 0,
                    report.reportingType || 'FULL',
                  ),
                  programName: programName,
                  report: report,
                },
                interimUpdate:
                  report.allocations?.[0]?.investment?.reportDate5 || 'none',
                interimUpdateDates: report.allocations.map(
                  a => a.investment?.reportDate5 || null,
                ),
                allocations: report.allocations,
                firstFunder: allocation
                  ? {
                      ...firstPortfolio?.funder,
                      email: firstDonorEmail,
                    }
                  : undefined,
                firstFunderPortfolio: firstPortfolio,
              }}
            />
          </div>
        </div>
        <div>
          {firstAllocationId && firstInvestmentId && firstDonorEmail && (
            <TrueImpactText variant="body-small" className="donor-portal-info">
              <div>
                <strong>Donor email:</strong> {firstDonorEmail}
              </div>
              <div>
                <strong>Donor Portal:</strong>{' '}
                <a
                  target="_blank"
                  href={donorPortalUrl}
                  rel="noopener noreferrer"
                >
                  {donorPortalUrl}
                </a>
              </div>
            </TrueImpactText>
          )}
        </div>
        <div className="question-wrapper">
          <div
            className="answer-wrapper"
            style={{ marginBottom: SPACERS.tiny }}
          >
            <ReportStatusModal
              dbId={report.dbId}
              year={report.year}
              stage={report.stage}
              status={report.status}
              feedbackType={report.feedbackType}
              reportingType={report.reportingType}
              optedInFullReporting={report.optedInFullReporting}
              width={284}
              allowEdit={isGhosting}
              className="report-status"
            />
          </div>
        </div>
        <AnswersList
          report={report}
          answers={answers}
          programName={programName}
          isReportPassed={isReportPassed}
          setAnswers={setAnswers}
        />
        <AddAnswerModal
          report={report}
          open={openAddAnswerModal}
          programName={programName}
          isReportPassed={isReportPassed}
          setAnswers={setAnswers}
          handleClose={() => setOpenAddAnswerModal(false)}
        />
        <div className="add-answer-button-wrapper">
          <TrueImpactButton
            className="add-answer-button"
            onClick={() => setOpenAddAnswerModal(true)}
          >
            <FontAwesomeIcon icon={faCirclePlus} />
            Add Answer
          </TrueImpactButton>
        </div>
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default AnswersModal;
